"use client";

import { Loader, Minus } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-slate-200 p-8">
        <p className="font-default text-xs uppercase tracking-[0.22em] text-blue-700 mb-4">
          § MESSAGE / RECEIVED
        </p>
        <div className="h-px w-32 bg-gradient-to-r from-blue-700 to-transparent mb-6" />
        <p className="font-default text-2xl md:text-3xl font-light text-slate-900 leading-tight mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-slate-200 bg-transparent text-slate-900 hover:bg-transparent hover:border-blue-700 font-default uppercase tracking-[0.2em] text-xs h-11 px-6">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-7">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default uppercase text-[0.6875rem] tracking-[0.22em] text-blue-700">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Sunil Singh" className="border-0 border-b border-slate-200 bg-transparent px-0 pb-3 h-auto focus-visible:border-blue-700 focus-visible:ring-0 placeholder:text-slate-400 text-slate-900" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default uppercase text-[0.6875rem] tracking-[0.22em] text-blue-700">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="contact@hotel.com" className="border-0 border-b border-slate-200 bg-transparent px-0 pb-3 h-auto focus-visible:border-blue-700 focus-visible:ring-0 placeholder:text-slate-400 text-slate-900" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone is required"
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default uppercase text-[0.6875rem] tracking-[0.22em] text-blue-700">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+91 98765 43210" className="border-0 border-b border-slate-200 bg-transparent px-0 pb-3 h-auto focus-visible:border-blue-700 focus-visible:ring-0 placeholder:text-slate-400 text-slate-900" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
        <FormField control={control} name="property_type" rules={{
        required: "Property type is required"
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default uppercase text-[0.6875rem] tracking-[0.22em] text-blue-700">
                Property Type
              </FormLabel>
              <FormControl>
                <Input placeholder="Hotel / Resort" className="border-0 border-b border-slate-200 bg-transparent px-0 pb-3 h-auto focus-visible:border-blue-700 focus-visible:ring-0 text-slate-900" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service_interest" rules={{
        required: "Service is required"
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default uppercase text-[0.6875rem] tracking-[0.22em] text-blue-700">
                Service Interest
              </FormLabel>
              <FormControl>
                <Input placeholder="Revenue Audit" className="border-0 border-b border-slate-200 bg-transparent px-0 pb-3 h-auto focus-visible:border-blue-700 focus-visible:ring-0 placeholder:text-slate-400 text-slate-900" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default uppercase text-[0.6875rem] tracking-[0.22em] text-blue-700">
              Project Details
            </FormLabel>
            <FormControl>
              <Textarea rows={3} placeholder="Tell us about your revenue goals..." className="border-0 border-b border-slate-200 bg-transparent px-0 pb-3 focus-visible:border-blue-700 focus-visible:ring-0 placeholder:text-slate-400 text-slate-900 resize-none min-h-[80px]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border border-red-600/40 p-4">
          <p className="text-red-600 text-sm font-default">{submitError}</p>
        </div>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-6">
        <Button type="submit" disabled={isSubmitting} className="rounded-lg bg-blue-700 text-white hover:bg-blue-800 font-default uppercase tracking-[0.2em] text-xs h-12 px-8">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending
            </> : "Request Consultation"}
        </Button>
        <p className="font-default uppercase text-[0.6875rem] tracking-[0.18em] text-slate-500 flex items-center gap-2">
          <span className="inline-block size-[6px] rounded-lg bg-blue-700" />
          We respond within 24 hours
        </p>
      </div>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1807" id="revenuejourneycontact" className="bg-slate-50 text-slate-900 py-24 md:py-32 lg:py-44 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 transition-all duration-[900ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7 relative">
            <div className="relative aspect-[16/10] lg:aspect-auto lg:h-full min-h-[420px] overflow-hidden group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_consulting_strategy.png" alt="Professional hotel revenue management strategy session" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-[1.03] rounded-lg shadow-lg" />
              <div className="absolute inset-0 pointer-events-none rounded-lg" style={{
              background: "radial-gradient(circle at 50% 50%, rgba(29, 78, 216, 0.15) 0%, transparent 65%)"
            }} />
              <div className="absolute bottom-6 left-6 md:bottom-8 md:left-8 max-w-xs">
                <div className="h-px w-32 mb-3 bg-gradient-to-r from-blue-700 to-transparent" />
                <p className="font-default uppercase text-[0.6875rem] tracking-[0.22em] text-white/90 leading-relaxed">
                  Data-Driven Revenue Growth
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative lg:py-4">
            <div aria-hidden="true" className="pointer-events-none absolute -top-4 -right-2 select-none font-default font-bold leading-none text-slate-900/[0.04]" style={{
            fontSize: "clamp(8rem, 18vw, 16rem)"
          }}>
              01
            </div>

            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <p className="font-default uppercase text-[0.6875rem] tracking-[0.22em] text-blue-700">
                  § 01 / Contact — Start Your Journey
                </p>
              </div>
              <div className="h-px w-48 mb-8 bg-gradient-to-r from-blue-700 to-transparent" />

              <h2 className="font-default font-bold text-slate-900 leading-[1.02] tracking-tight mb-6" style={{
              fontSize: "clamp(2.25rem, 5vw, 4.5rem)"
            }}>
                Maximize your hotel's{" "}
                <em className={`italic text-blue-700 transition-opacity duration-500 ${isVisible ? "opacity-100" : "opacity-0"}`} style={{
                transitionDelay: "200ms"
              }}>
                  revenue
                </em>
                .
              </h2>

              <p className="font-default text-base md:text-lg text-slate-600 leading-relaxed mb-10 max-w-md">
                Partner with Sunil Singh to implement data-driven OTA strategies and optimize your property's performance.
              </p>

              <FormProvider formKey="revenue_journey_form_contact" formId="revenue_journey_form_contact" sectionName="revenuejourneycontact" defaultValues={{
              name: "",
              email: "",
              phone: "",
              property_type: "",
              service_interest: "",
              special_requests: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              property_type: {
                required: true
              },
              service_interest: {
                required: true
              }
            }} submitText="Request Consultation" successMessage="Thank you. Your inquiry has been received. We will contact you shortly to discuss your revenue goals." errorMessage="Something went wrong. Please try again or email us directly.">
                <ReservationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}